import { AfterViewInit, Component, computed, DestroyRef, effect, inject, input, OnInit, output, Signal, signal } from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';

import { AppError, ButtonModule, SimpleInputModule, SpinnerService } from '@hglobal/common';
import { FormBuilder, FormControl, FormGroup } from '@ngneat/reactive-forms';
import { GroupResolverFormBuilder } from '@ngneat/reactive-forms/lib/form-builder';
import { TranslateModule } from '@ngx-translate/core';

import { SearchSelectComponent } from '@pp/common';
import { AuthStore } from '@pp/core/auth-store';
import { Account, checkIsAddressesTheSame, GeoLocationForApi, getLocationDataFromObj } from '@pp/core/entities';
import { postalCodeValidator } from '@pp/core/helpers';
import { GlobalToastService } from '@pp/core/services';
import { DictionariesStore } from '@pp/dictionaries';

@Component({
  selector: 'pp-default-address',
  imports: [FormsModule, ReactiveFormsModule, SearchSelectComponent, SimpleInputModule, TranslateModule, ButtonModule],
  templateUrl: './default-address.component.html',
  styleUrl: './default-address.component.scss',
})
export class DefaultAddressComponent implements OnInit, AfterViewInit {
  readonly #fb = inject(FormBuilder);
  readonly #dictionariesStore = inject(DictionariesStore);
  readonly #authStore = inject(AuthStore);
  readonly #destroyRef = inject(DestroyRef);
  readonly #spinner = inject(SpinnerService);
  readonly #globalToastService = inject(GlobalToastService);

  readonly user = input<Account>();
  readonly readonlyRights = input<boolean>();
  readonly closeDialog = output<boolean>();
  readonly unsavedChanges = output<boolean>();

  countriesList = this.#dictionariesStore.countries;
  hasChanges = signal<boolean>(false);
  isLoading = this.#authStore.isLoading;
  addressForm: FormGroup<GroupResolverFormBuilder<GeoLocationForApi>>;
  errorDictionary: Record<string, string> = {
    invalidCode: 'address.form.validation.error.postal',
  };

  initialAddress: Signal<GeoLocationForApi> = computed(() => getLocationDataFromObj({ ...this.user(), postal: this.user().postalCode }));

  unsavedChangesEffect = effect(() => {
    this.unsavedChanges.emit(this.hasChanges());
  });

  setAddressSuccessEffect = effect(() => {
    const success = this.#authStore.isSetAddressSuccess();
    if (success) {
      this.#handleSavingAddressSuccess();
    }
  });

  setAddressErrorEffect = effect(() => {
    const error = this.#authStore.setAddressError();
    if (error) {
      this.#handleSavingAddressError(error);
    }
  });

  get countryCtrl(): FormControl<string> {
    return this.addressForm?.get('country');
  }

  get countryCodeCtrl(): FormControl<string> {
    return this.addressForm?.get('countryCode');
  }

  get postalCtrl(): FormControl<string> {
    return this.addressForm?.get('postal');
  }

  constructor() {
    this.#spinner.observe(toObservable(this.isLoading));
  }

  ngOnInit() {
    this.#initForm();
    this.setInitialValues();
    this.#subscribeOnCountryControl();
    this.#subscribeOnCountryCodeControl();
  }

  ngAfterViewInit() {
    this.#subscribeOnFormChanges();
  }

  setInitialValues(): void {
    this.addressForm.patchValue(this.initialAddress());
    this.addressForm.markAsUntouched();
  }

  submitForm(): void {
    if (this.readonlyRights()) {
      return;
    }
    this.#authStore.setAddress(this.addressForm.getRawValue());
  }

  #handleSavingAddressError(error: AppError): void {
    this.#globalToastService.showErrorWithTitle(error.originalError?.error?.error_description);
  }

  #handleSavingAddressSuccess(): void {
    this.#globalToastService.showSuccessWithTitle('address.form.successMessage');
    this.closeDialog.emit(true);
    this.#authStore.resetRequestStatus('setAddress');
  }

  #initForm(): void {
    this.addressForm = this.#fb.group({
      country: [<string>null, Validators.required],
      countryCode: [<string>null, Validators.required],
      city: [<string>null, Validators.required],
      region: [<string>null, Validators.required],
      street: [<string>null, Validators.required],
      houseNumber: [<string>null, Validators.required],
      apartment: [<string>null, Validators.required],
      postal: [<string>null, Validators.required],
    });

    this.postalCtrl.addValidators(postalCodeValidator(this.countryCodeCtrl));
  }

  #subscribeOnCountryControl(): void {
    this.countryCtrl.valueChanges.pipe(takeUntilDestroyed(this.#destroyRef)).subscribe(countryName => {
      const selectedCountry = countryName && this.countriesList().find(country => country.countryName === countryName);
      this.countryCodeCtrl.setValue(selectedCountry?.countryCode || null);
    });
  }

  #subscribeOnCountryCodeControl(): void {
    this.countryCodeCtrl.valueChanges.pipe(takeUntilDestroyed(this.#destroyRef)).subscribe(() => this.postalCtrl.updateValueAndValidity());
  }

  #subscribeOnFormChanges(): void {
    this.addressForm.valueChanges.pipe(takeUntilDestroyed(this.#destroyRef)).subscribe(() => this.#checkOnChanges());
  }

  #checkOnChanges(): void {
    const newAddress = this.addressForm.getRawValue();
    this.hasChanges.set(!checkIsAddressesTheSame(newAddress, this.initialAddress()));
  }
}
